import {
  Breakpoints,
  ButtonSize,
  Carousel,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import React, { type FC, useEffect, useState } from 'react';

import { type Scalars } from '../../__generated-gql-types__/globalTypes';
import { initializeApollo } from '../../lib/apolloClient';
import TabbedNavigation from '../common/TabbedNavigation';

import { VerticalCoachCard } from './VerticalCoachCard';
import {
  type CoachesTabsCoachFragment,
  CoachesTabsGetCoachesDocument,
  type CoachesTabsGetCoachesQuery,
  type CoachesTabsGetCoachesQueryVariables,
  type CoachesTabsHydratedCoachFragment,
  useCoachesTabsGetCoachesQuery,
  useCoachesTabsGetHydratedCoachesQuery,
} from './__generated-gql-types__/CoachesTabs.generated';

enum CoachTab {
  POPULAR,
  PROFESSIONAL,
  BUDGET_FRIENDLY,
  RECENTLY_VIEWED,
}

export interface CoachesTabsDataProps {
  coaches: Possible<CoachesTabsGetCoachesQuery>;
}

export const getStaticCoachesTabsProps = async ({
  categoryUrn,
  subCategoryUrn,
}: {
  categoryUrn: Possible<Scalars['CategoryUrn']['output']>;
  subCategoryUrn: Possible<Scalars['SubCategoryUrn']['output']>;
}): Promise<CoachesTabsDataProps> => {
  const apolloClient = initializeApollo();
  const { data } = await apolloClient.query<
    Possible<CoachesTabsGetCoachesQuery>,
    CoachesTabsGetCoachesQueryVariables
  >({
    query: CoachesTabsGetCoachesDocument,
    variables: {
      categoryUrn,
      subCategoryListFilter: subCategoryUrn,
    },
  });
  return {
    coaches: data,
  };
};

interface CoachesTabsProps {
  categoryUrn: Scalars['CategoryUrn']['output'];
  bookedCoaches: CoachesTabsCoachFragment[];
  initialData: CoachesTabsDataProps;
  className: string;
  subCategoryListFilter?: Possible<Scalars['CategoryUrn']['output']>;
}

export const CoachesTabs: FC<CoachesTabsProps> = ({
  categoryUrn,
  bookedCoaches,
  initialData,
  className,
  subCategoryListFilter,
}) => {
  const isMd = useMediaQuery(Breakpoints.MD);
  const isLg = useMediaQuery(Breakpoints.LG);

  const [currentStep, setCurrentStep] = useState<CoachTab>(CoachTab.POPULAR);

  const { data: clientCoachesData } = useCoachesTabsGetCoachesQuery({
    variables: { categoryUrn, subCategoryListFilter },
    skip: !!initialData,
  });

  const coachesData = initialData.coaches ?? clientCoachesData;

  const coachSlugs = [
    ...(coachesData?.budgetFriendlyCoaches.results ?? []).map(
      (coach) => coach.slug,
    ),
    ...(coachesData?.professionalCoaches.results ?? []).map(
      (coach) => coach.slug,
    ),
    ...(coachesData?.popularCoaches.results ?? []).map((coach) => coach.slug),
  ];

  const { data: hydratedCoachesData } = useCoachesTabsGetHydratedCoachesQuery({
    variables: { coachSlugs },
    // skip if we don't have initial data because that means we are already fetching in the client
    skip: !initialData,
  });

  const hydratedCoachesById = (
    hydratedCoachesData?.coachesBySlugs ?? []
  ).reduce<
    Record<Scalars['CoachUrn']['output'], CoachesTabsHydratedCoachFragment>
  >((acc, coach) => {
    acc[coach.id] = coach;
    return acc;
  }, {});

  const { popularCoaches, professionalCoaches, budgetFriendlyCoaches } = {
    popularCoaches: (coachesData?.popularCoaches.results ?? []).map(
      (coach) => ({
        ...coach,
        ...(hydratedCoachesById[coach.id] ?? {}),
      }),
    ),
    professionalCoaches: (coachesData?.professionalCoaches.results ?? []).map(
      (coach) => ({
        ...coach,
        ...(hydratedCoachesById[coach.id] ?? {}),
      }),
    ),
    budgetFriendlyCoaches: (
      coachesData?.budgetFriendlyCoaches.results ?? []
    ).map((coach) => ({
      ...coach,
      ...(hydratedCoachesById[coach.id] ?? {}),
    })),
  };

  const coaches =
    currentStep === CoachTab.POPULAR
      ? popularCoaches
      : currentStep === CoachTab.PROFESSIONAL
        ? professionalCoaches
        : currentStep === CoachTab.BUDGET_FRIENDLY
          ? budgetFriendlyCoaches
          : currentStep === CoachTab.RECENTLY_VIEWED
            ? bookedCoaches
            : [];

  const steps = [
    ...(popularCoaches.length > 0
      ? [
          {
            tab: CoachTab.POPULAR,
            label: 'Popular',
            isActive: currentStep === CoachTab.POPULAR,
          },
        ]
      : []),
    ...(professionalCoaches.length > 0
      ? [
          {
            tab: CoachTab.PROFESSIONAL,
            label: 'Professional',
            isActive: currentStep === CoachTab.PROFESSIONAL,
          },
        ]
      : []),
    ...(budgetFriendlyCoaches.length > 0
      ? [
          {
            tab: CoachTab.BUDGET_FRIENDLY,
            label: 'Budget-friendly',
            isActive: currentStep === CoachTab.BUDGET_FRIENDLY,
          },
        ]
      : []),
    ...(bookedCoaches.length > 0
      ? [
          {
            tab: CoachTab.RECENTLY_VIEWED,
            label: 'Recently viewed',
            isActive: currentStep === CoachTab.RECENTLY_VIEWED,
          },
        ]
      : []),
  ];

  useEffect(
    () =>
      setCurrentStep(
        popularCoaches.length > 0
          ? CoachTab.POPULAR
          : professionalCoaches.length > 0
            ? CoachTab.PROFESSIONAL
            : budgetFriendlyCoaches.length > 0
              ? CoachTab.BUDGET_FRIENDLY
              : CoachTab.RECENTLY_VIEWED,
      ),
    [],
  );

  return coaches.length > 0 ? (
    <div className={`lg:w-full ${className}`}>
      <h2 className="text-2xl font-medium text-leland-gray-dark">
        Get started with a coach
      </h2>
      <div className="mt-8 flex justify-start space-x-8 overflow-x-auto border-b border-leland-gray-stroke">
        {steps.map((step, index) => (
          <TabbedNavigation
            key={index}
            label={step.label}
            isActive={step.isActive}
            onClick={() => setCurrentStep(step.tab)}
          />
        ))}
      </div>
      <div className="mt-6 flex">
        <Carousel
          key={currentStep}
          swiperProps={{
            slidesPerGroupAuto: false,
            spaceBetween: 20,
            slidesPerView: isLg ? 4 : isMd ? 3 : 1.15,
          }}
          buttonSize={ButtonSize.MEDIUM}
          disableArrowGradient
          items={coaches.map((coach) => (
            <VerticalCoachCard
              key={coach.id}
              coach={coach}
              categoryUrn={categoryUrn}
            />
          ))}
        />
      </div>
    </div>
  ) : null;
};
